import {
  MIN_HEIGHT_ARC_MM,
  MIN_HEIGHT_BASE_OF_ARC_MM,
} from '@cutr/constants/windows/presets/resizers.ts';
import { RectangleArcTopFrame } from '@cutr/constants/windows/windows-geometry.ts';

import { MIN_SASH_HEIGHT_MM } from './dimensions';
import { events } from './events';
import { useKonva } from './KonvaContext';
import { LineInput } from './primitives/LineInput';
import { Box } from './types';

type ArcHeightInputProps = {
  name: 'heightBaseOfArc' | 'heightArc';
  box: Box;
  min: number;
  max: number;
};

const ArcHeightInput = ({ name, box, min, max }: ArcHeightInputProps) => {
  const { x, y, width } = box;
  const { stageRef } = useKonva();
  const lineBox = { ...box, x: x + width / 2, y };

  const onSubmit = (value: number) => {
    stageRef.current?.fire(events.frameDimensionChange, { value, name }, true);
  };

  return (
    <LineInput
      value={box.height}
      min={min}
      max={max}
      direction="vertical"
      box={lineBox}
      options={{ altTriangle: true }}
      onSubmit={onSubmit}
    />
  );
};

export type ShapedTopInputsProps = {
  frameY: number;
  areaTopMM: number;
  areaBelowHeightMM: number;
};

export const ArcTopAreaHeightInputs = ({
  frameShape,
  frameY,
  areaTopMM,
  areaBelowHeightMM,
}: ShapedTopInputsProps & { frameShape: RectangleArcTopFrame }) => {
  const { renderStyles } = useKonva();
  const { leftMM: x } = renderStyles.frameDimensionLineOffset;
  const { leftMM: width } = renderStyles.areaDimensionLineOffset;

  const baseHeight = frameShape.heightMM - areaTopMM;

  return (
    <>
      <ArcHeightInput
        key={`top-area-base-input-${baseHeight}`}
        name="heightBaseOfArc"
        box={{ x, y: frameY + areaTopMM, width, height: baseHeight }}
        min={MIN_HEIGHT_BASE_OF_ARC_MM}
        max={baseHeight + areaBelowHeightMM - MIN_SASH_HEIGHT_MM}
      />
      <ArcHeightInput
        key={`top-area-arc-input-${frameShape.heightArcMM}`}
        name="heightArc"
        box={{
          x,
          y: frameY + frameShape.heightMM,
          width,
          height: frameShape.heightArcMM,
        }}
        min={MIN_HEIGHT_ARC_MM}
        max={Math.min(
          frameShape.widthMM / 2,
          frameShape.heightArcMM + baseHeight - MIN_HEIGHT_BASE_OF_ARC_MM
        )}
      />
    </>
  );
};
